import { useActivatePriceList, usePriceList, type PriceListVersion } from "../../api/admin";
import { formatDateTime } from "../../api/dates";
import { formatMoney, formatPercent } from "../../api/money";
import { Card, StatusChip, type Tone } from "../../components/ui";
import { ServiceKitsPanel } from "./ServiceKitsPanel";

const STATUS_TONE: Record<PriceListVersion["status"], Tone> = { active: "ok", draft: "draft", superseded: "quiet" };
const STATUS_WORD: Record<PriceListVersion["status"], string> = { active: "In force", draft: "Draft", superseded: "Superseded" };

export function PriceListStatus({ status }: { status: PriceListVersion["status"] }) {
  return <StatusChip tone={STATUS_TONE[status]}>{STATUS_WORD[status]}</StatusChip>;
}

/** One version of a supplier's list: its parts as they were read, and who
 * uploaded and activated it. Putting it in force supersedes the one before,
 * which is why the button says what it does rather than "Activate". */
export function PriceListDetailView({ id }: { id: number }) {
  const view = usePriceList(id);
  const activate = useActivatePriceList(id);
  if (view.isPending) {
    return <div className="table-card empty-state">Loading the version…</div>;
  }
  if (view.isError) {
    return <p className="notice bad">This version could not be loaded: {view.error.message}</p>;
  }
  const d = view.data;
  return (
    <Card
      title={`${d.supplier?.name ?? "Supplier"} v${d.price_list.version_no}`}
      sub={
        <>
          {d.price_list.filename} · {d.price_list.sheet}
          {d.price_list.discount_pct ? ` · discount ${formatPercent(d.price_list.discount_pct)}` : ""}
        </>
      }
      actions={
        <>
          <PriceListStatus status={d.price_list.status} />
          {d.can_activate ? (
            <button type="button" className="btn btn-primary" disabled={activate.isPending} onClick={() => activate.mutate()}>
              Put this version in force
            </button>
          ) : null}
        </>
      }
      flush
    >
      {activate.isError ? <p className="notice bad detail-notice">{activate.error.message}</p> : null}
      {d.price_list.notes ? <p className="muted-note detail-notice">{d.price_list.notes}</p> : null}
      <div className="detail-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Part</th>
              <th>Size</th>
              <th>SAP code</th>
              <th className="num">Cost</th>
              <th className="num">Selling</th>
            </tr>
          </thead>
          <tbody>
            {d.items.map((i) => (
              <tr key={i.id}>
                <td className="mono">{i.supplier_item_code ?? "—"}</td>
                <td>{i.part_description}</td>
                <td>{i.size}</td>
                <td className="mono muted">{i.sap_code ?? "—"}</td>
                <td className="num">{formatMoney(i.unit_cost)}</td>
                <td className="num">{formatMoney(i.selling_price)}</td>
              </tr>
            ))}
            {d.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  No parts were read from this version.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <ServiceKitsPanel kits={d.kits} />
      <div className="card-body">
        <h3 className="section-label">History</h3>
        <ul className="history-list">
          {d.events.map((e, i) => (
            <li key={i}>
              <span className="muted">{formatDateTime(e.at)}</span> · {e.action}
              {e.by ? ` · ${e.by}` : ""}
              {e.detail ? ` · ${e.detail}` : ""}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
